import React,{useEffect,useMemo,useState} from 'react';
import {cmsService} from '../../services/cmsService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiX,FiSearch,FiMail,FiMessageSquare,FiSend,FiCheckCircle,FiAlertCircle,FiRefreshCw,FiStar,FiChevronDown}=FiIcons;
const DEFAULT_PHONE_PREFIX='+61';
const normalizeAustralianPhone=(value)=> {
const normalizedValue=value.trim();
if (!normalizedValue) {
return DEFAULT_PHONE_PREFIX;
}
const compactValue=normalizedValue.replace(/[()\s-]/g,'');
if (compactValue===DEFAULT_PHONE_PREFIX) {
return DEFAULT_PHONE_PREFIX;
}
if (compactValue.startsWith('+610')) {
return `+61${compactValue.slice(4).replace(/\D/g,'')}`;
}
if (compactValue.startsWith('04')) {
return `+61${compactValue.slice(1).replace(/\D/g,'')}`;
}
if (compactValue.startsWith('61')) {
return `+${compactValue.replace(/\D/g,'')}`;
}
if (compactValue.startsWith('+')) {
return `+${compactValue.slice(1).replace(/\D/g,'')}`;
}
const digitsOnly=compactValue.replace(/\D/g,'');
if (digitsOnly.startsWith('0')) {
return `+61${digitsOnly.slice(1)}`;
}
return `+61${digitsOnly}`;
};
const normalizeSmsRecipientList=(value)=> {
const rawRecipients=value
.split(/[\n,;]+/)
.map((recipient)=> recipient.trim())
.filter(Boolean);
if (!rawRecipients.length) {
return DEFAULT_PHONE_PREFIX;
}
const normalizedRecipients=rawRecipients
.map((recipient)=> normalizeAustralianPhone(recipient))
.filter(Boolean);
return [...new Set(normalizedRecipients)].join('\n');
};
const getValidSmsRecipients=(value)=> value
.split(/[\n,;]+/)
.map((recipient)=> normalizeAustralianPhone(recipient))
.filter((recipient)=> /^\+\d{8,15}$/.test(recipient));
const normalizeEmailRecipientList=(value)=> {
const normalizedRecipients=value
.split(/[\n,;]+/)
.map((recipient)=> recipient.trim().toLowerCase())
.filter(Boolean);
return [...new Set(normalizedRecipients)].join('\n');
};
const IntroOutreachComposer=({isOpen,onClose,adminPasscode})=> {
const [loading,setLoading]=useState(false);
const [sending,setSending]=useState(false);
const [approvingPreviewId,setApprovingPreviewId]=useState('');
const [availablePreviews,setAvailablePreviews]=useState([]);
const [searchText,setSearchText]=useState('');
const [selectedPreviewIds,setSelectedPreviewIds]=useState([]);
const [primaryPreviewId,setPrimaryPreviewId]=useState('');
const [contactName,setContactName]=useState('');
const [businessName,setBusinessName]=useState('');
const [emailRecipients,setEmailRecipients]=useState('');
const [smsRecipients,setSmsRecipients]=useState(DEFAULT_PHONE_PREFIX);
const [emailSubject,setEmailSubject]=useState('');
const [emailMessage,setEmailMessage]=useState('');
const [smsMessage,setSmsMessage]=useState('');
const [consentConfirmed,setConsentConfirmed]=useState(false);
const [consentMethod,setConsentMethod]=useState('Phone conversation');
const [stageMessages,setStageMessages]=useState([]);
const [error,setError]=useState('');
const [selectionError,setSelectionError]=useState('');
const appendStage=(message,status='info')=> {
setStageMessages((current)=> [
...current,
{
id: `${Date.now()}_${Math.random()}`,
message,
status,
},
]);
};
const createRequestId=()=> `intro_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
useEffect(()=> {
if (isOpen) {
loadActivePreviews();
}
},[isOpen]);
const loadActivePreviews=async ()=> {
setLoading(true);
setError('');
try {
const [allPreviewsResult,approvedPreviewsResult]=await Promise.all([
cmsService.listPreviews(adminPasscode),
cmsService.listIntroOutreachPreviews(adminPasscode),
]);
setLoading(false);
if (!allPreviewsResult.success) {
setError(
allPreviewsResult.error ||
'Unable to load active previews.'
);
return;
}
if (!approvedPreviewsResult.success) {
setError(
approvedPreviewsResult.error ||
'Unable to load approved outreach previews.'
);
return;
}
const allPreviews=
Array.isArray(allPreviewsResult.data)
? allPreviewsResult.data
: [];
const approvedList=
Array.isArray(approvedPreviewsResult.data?.previews)
? approvedPreviewsResult.data.previews
: [];
const approvedLookup=new Map(
approvedList.map((p)=> [p.previewId,p])
);
const activePreviews=allPreviews
.filter(
(preview)=>
String(preview.status || 'active')
.toLowerCase() === 'active'
)
.map((preview)=> {
const approvedData=approvedLookup.get(preview.previewId);
return {
...preview,
...approvedData,
outreachApproved: !!approvedData,
};
});
setAvailablePreviews(activePreviews);
const approvedPreviewIds=activePreviews
.filter((p)=> p.outreachApproved)
.map((p)=> p.previewId);
const autoSelectedIds=
approvedPreviewIds.length > 0 &&
approvedPreviewIds.length <= 3
? approvedPreviewIds
: [];
const autoPrimaryId=
autoSelectedIds[0] || '';
setSelectedPreviewIds(
autoSelectedIds
);
setPrimaryPreviewId(
autoPrimaryId
);
await refreshDraft({
nextPreviewIds:
autoSelectedIds,
nextPrimaryPreviewId:
autoPrimaryId,
});
} catch (err) {
setLoading(false);
setError('Unable to load previews for introductory outreach.');
}
};
const filteredPreviews=useMemo(()=> {
if (!searchText.trim()) {
return availablePreviews;
}
const search=searchText.toLowerCase();
return availablePreviews.filter((preview)=> {
const pageTitle=(preview.pageTitle || '').toLowerCase();
const clientName=(preview.clientName || '').toLowerCase();
const businessName=(preview.businessName || '').toLowerCase();
const previewId=(preview.previewId || '').toLowerCase();
return (
pageTitle.includes(search) ||
clientName.includes(search) ||
businessName.includes(search) ||
previewId.includes(search)
);
});
},[availablePreviews,searchText]);
const handlePreviewSelection=async (
previewId
)=> {
setSelectionError('');
const isSelected=
selectedPreviewIds.includes(
previewId
);
const nextIds=
isSelected
? selectedPreviewIds.filter(
(id)=> id !== previewId
)
: [
...selectedPreviewIds,
previewId,
];
if (nextIds.length > 3) {
setSelectionError(
'Select no more than three previews.'
);
return;
}
const nextPrimaryId=
nextIds.includes(
primaryPreviewId
)
? primaryPreviewId
: nextIds[0] || '';
setSelectedPreviewIds(
nextIds
);
setPrimaryPreviewId(
nextPrimaryId
);
await refreshDraft({
nextPreviewIds:
nextIds,
nextPrimaryPreviewId:
nextPrimaryId,
});
};
const handlePrimaryChange=async (
previewId
)=> {
setPrimaryPreviewId(
previewId
);
await refreshDraft({
nextPreviewIds:
selectedPreviewIds,
nextPrimaryPreviewId:
previewId,
});
};
const approveAndSelectPreview=async (
previewId
)=> {
if (
!previewId ||
approvingPreviewId
) {
return;
}
if (
!selectedPreviewIds.includes(previewId) &&
selectedPreviewIds.length >= 3
) {
setSelectionError(
'Select no more than three previews.'
);
return;
}
setApprovingPreviewId(previewId);
setSelectionError('');
setError('');
const approvalResult=
await cmsService.setPreviewOutreachApproved(
previewId,
true,
adminPasscode
);
if (!approvalResult.success) {
setApprovingPreviewId('');
setError(
approvalResult.error ||
'Unable to approve this preview for outreach use.'
);
return;
}
const approvedResult=
await cmsService.listIntroOutreachPreviews(
adminPasscode
);
if (!approvedResult.success) {
setApprovingPreviewId('');
setError(
approvedResult.error ||
'Unable to confirm outreach approval.'
);
return;
}
const approvedPreviews=
Array.isArray(
approvedResult.data?.previews
)
? approvedResult.data.previews
: [];
const approvedPreview=
approvedPreviews.find(
(preview)=>
preview.previewId === previewId
);
if (!approvedPreview) {
setApprovingPreviewId('');
setError(
'This preview could not be approved for outreach use. Confirm that it is active and has not expired.'
);
return;
}
setAvailablePreviews(
(current)=>
current.map(
(preview)=>
preview.previewId === previewId
? {
...preview,
...approvedPreview,
outreachApproved: true,
}
: preview
)
);
const nextIds=
selectedPreviewIds.includes(
previewId
)
? selectedPreviewIds
: [
...selectedPreviewIds,
previewId,
];
const nextPrimaryId=
primaryPreviewId ||
previewId;
setSelectedPreviewIds(nextIds);
setPrimaryPreviewId(nextPrimaryId);
setApprovingPreviewId('');
await refreshDraft({
nextPreviewIds: nextIds,
nextPrimaryPreviewId:
nextPrimaryId,
});
};
const refreshDraft=async ({
nextPreviewIds=selectedPreviewIds,
nextPrimaryPreviewId=primaryPreviewId,
}={})=> {
const result=await cmsService.getIntroOutreachDraft(
{
contactName: contactName.trim(),
businessName: businessName.trim(),
selectedPreviewIds: nextPreviewIds,
primaryPreviewId: nextPrimaryPreviewId,
},
adminPasscode
);
if (result.success) {
const data=result.data || {};
setEmailSubject(
data.emailSubject || ''
);
setEmailMessage(
data.emailMessage || ''
);
setSmsMessage(
data.smsMessage || ''
);
setPrimaryPreviewId(
data.primaryPreviewId ||
nextPrimaryPreviewId ||
''
);
} else {
setError(
result.error ||
'Unable to generate introductory outreach draft.'
);
}
};
const sendOutreach=async ({includeSms})=> {
setError('');
setStageMessages([]);
if (selectedPreviewIds.length===0) {
setError('Please select at least one preview.');
return;
}
if (!primaryPreviewId) {
setError('Please select a primary SMS preview.');
return;
}
if (!normalizeEmailRecipientList(emailRecipients)) {
setError('Please provide at least one email recipient.');
return;
}
if (!consentConfirmed) {
setError('You must confirm the appropriate basis for sending.');
return;
}
if (includeSms) {
const validSms=getValidSmsRecipients(smsRecipients);
if (validSms.length===0) {
setError('Please enter at least one complete SMS recipient number in international format,for example +61425228778.');
return;
}
}
const outreachRequestId=createRequestId();
setSending(true);
let hadError=false;
try {
if (includeSms) {
appendStage('Preparing introductory email...');
appendStage('Building selected visual-example cards...');
appendStage('Sending introductory email...');
} else {
appendStage('Preparing introductory email...');
appendStage('Sending introductory email...');
}
const emailResult=await cmsService.sendIntroOutreachEmail(
{
outreachRequestId,
channelRequestId: `${outreachRequestId}_email`,
contactName: contactName.trim(),
businessName: businessName.trim(),
emailRecipients: normalizeEmailRecipientList(emailRecipients),
selectedPreviewIds,
primaryPreviewId,
emailSubject,
emailMessage,
consentConfirmed,
consentMethod,
},
adminPasscode
);
if (emailResult.success) {
appendStage('Introductory email sent successfully. Internal CC applied.','success');
} else {
hadError=true;
appendStage(`Email failed: ${emailResult.error}`,'error');
}
if (includeSms) {
appendStage('Preparing introductory SMS...');
appendStage('Sending introductory SMS...');
const smsResult=await cmsService.sendIntroOutreachSms(
{
outreachRequestId,
channelRequestId: `${outreachRequestId}_sms`,
contactName: contactName.trim(),
businessName: businessName.trim(),
smsRecipients: getValidSmsRecipients(smsRecipients).join('\n'),
selectedPreviewIds,
primaryPreviewId,
smsMessage,
consentConfirmed,
consentMethod,
},
adminPasscode
);
if (smsResult.success) {
appendStage('Introductory SMS sent successfully. Internal confirmation processed.','success');
} else {
hadError=true;
appendStage(`SMS failed: ${smsResult.error}`,'error');
}
}
appendStage(
hadError
? 'Introductory outreach completed with an error. Review the stages above.'
: 'Introductory outreach completed successfully.',
hadError ? 'error' : 'success'
);
} catch (err) {
setError('A network error occurred during the sending process.');
} finally {
setSending(false);
}
};
if (!isOpen) {
return null;
}
return (
<div className="fixed inset-0 z-[150] flex items-center justify-center bg-espresso/65 backdrop-blur-md px-4 py-6">
<div className="w-full max-w-6xl rounded-[32px] border border-gold/30 bg-ivory shadow-2xl flex flex-col max-h-full overflow-hidden">
{/* Modal Header */}
<div className="shrink-0 border-b border-gold/15 bg-paper px-6 py-5 sm:px-8 flex justify-between items-center">
<div className="flex items-center gap-4">
<div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center">
<SafeIcon icon={FiSend} className="w-5 h-5 text-gold" />
</div>
<div>
<h2 className="text-xl luxury-serif-heading text-espresso">Create Introductory Outreach</h2>
<p className="text-[10px] text-muted uppercase tracking-wider font-bold">Global Outreach Composer</p>
</div>
</div>
<button
onClick={onClose}
disabled={sending}
className="p-2 text-muted hover:text-espresso transition-colors disabled:opacity-50"
>
<SafeIcon icon={FiX} className="w-6 h-6" />
</button>
</div>
{/* Modal Body - Two Column Layout */}
<div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 sm:px-8 overscroll-contain">
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
{/* LEFT COLUMN */}
<div className="space-y-6">
{/* Contact Details */}
<div className="space-y-4">
<h3 className="text-sm font-bold uppercase tracking-widest text-gold mb-4">Contact Details</h3>
<div className="relative">
<input
type="text"
value={contactName}
onChange={(e)=> setContactName(e.target.value)}
onBlur={()=> refreshDraft()}
placeholder=" "
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
Contact Name
</label>
</div>
<div className="relative">
<input
type="text"
value={businessName}
onChange={(e)=> setBusinessName(e.target.value)}
onBlur={()=> refreshDraft()}
placeholder=" "
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
Business Name
</label>
</div>
</div>
{/* Recipients */}
<div className="space-y-4">
<h3 className="text-sm font-bold uppercase tracking-widest text-gold mb-4">Recipients</h3>
<div className="relative">
<textarea
value={emailRecipients}
onChange={(e)=> setEmailRecipients(e.target.value)}
onBlur={()=> setEmailRecipients(normalizeEmailRecipientList(emailRecipients))}
placeholder="email@example.com"
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[80px]"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
Email Recipients
</label>
<p className="text-[9px] text-muted font-light mt-1">One email per line,comma or semicolon separated.</p>
</div>
<div className="relative">
<textarea
value={smsRecipients}
onChange={(e)=> {setSmsRecipients(e.target.value.replace(/[^\d+\s(),;\n-]/g,''));if (error) {setError('');}}}
onBlur={()=> setSmsRecipients(normalizeSmsRecipientList(smsRecipients))}
placeholder="+61425228778"
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[80px]"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
SMS Recipients
</label>
<p className="text-[9px] text-muted font-light mt-1">Use international format,for example +61425228778.</p>
</div>
</div>
{/* Preview Selection */}
<div className="space-y-4">
<div className="flex items-center justify-between">
<h3 className="text-sm font-bold uppercase tracking-widest text-gold">Select Previews</h3>
<span className={`text-[10px] font-bold uppercase tracking-wider ${selectedPreviewIds.length > 3 ? 'text-red-500' : 'text-muted'}`}>
{selectedPreviewIds.length}/3
</span>
</div>
<div className="relative">
<SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
<input
type="text"
value={searchText}
onChange={(e)=> setSearchText(e.target.value)}
placeholder="Search by title,client,business or ID..."
className="w-full bg-paper border border-gold/30 rounded-xl pl-12 pr-4 py-3 text-espresso caret-espresso outline-none transition focus:border-gold text-sm"
/>
</div>
{selectionError && (
<div className="flex items-center gap-2 text-red-500 text-[10px] bg-red-50 p-3 border border-red-200 rounded-soft">
<SafeIcon icon={FiAlertCircle} className="flex-shrink-0" />
{selectionError}
</div>
)}
<div className="space-y-3 max-h-[320px] overflow-y-auto pr-2">
{filteredPreviews.map((preview)=> {
const isSelected=selectedPreviewIds.includes(preview.previewId);
const isPrimary=primaryPreviewId===preview.previewId;
const isApproved=preview.outreachApproved === true;
const isApproving=approvingPreviewId === preview.previewId;
return (
<div
key={preview.previewId}
onClick={()=> isApproved ? handlePreviewSelection(preview.previewId) : null}
className={`p-4 border rounded-xl transition-all ${
isSelected
? isPrimary
? 'bg-gold/10 border-gold ring-2 ring-gold/30'
: 'bg-gold/5 border-gold/40'
: isApproved
? 'bg-paper border-gold/20 hover:border-gold/40'
: 'bg-cream/50 border-champagne/30'
} ${!isApproved ? 'cursor-default' : 'cursor-pointer'}`}
>
<div className="flex items-start justify-between mb-2">
<div className="min-w-0 flex-1">
<h4 className="font-serif text-sm text-espresso mb-1 truncate">{preview.pageTitle || 'Untitled Preview'}</h4>
<p className="text-[9px] text-muted font-mono">ID: {preview.previewId}</p>
{preview.clientName && (
<p className="text-[9px] text-muted mt-1">{preview.clientName}</p>
)}
<div className="flex items-center gap-2 mt-2">
{isApproved ? (
<span className="text-[8px] uppercase tracking-wider text-gold font-bold">Ready for Outreach</span>
) : (
<span className="text-[8px] uppercase tracking-wider text-muted font-bold">Approval Required</span>
)}
</div>
</div>
{isPrimary && (
<SafeIcon icon={FiStar} className="w-4 h-4 text-gold flex-shrink-0 ml-2" />
)}
</div>
{isApproved && isSelected && (
<div className="flex items-center gap-2 mt-2">
<label className="text-[9px] uppercase tracking-wider text-muted">Primary:</label>
<input
type="radio"
name="primaryPreview"
checked={isPrimary}
onChange={()=> handlePrimaryChange(preview.previewId)}
className="w-3 h-3 accent-gold"
onClick={(e)=> e.stopPropagation()}
/>
</div>
)}
{!isApproved && (
<div className="mt-3">
<button
type="button"
onClick={(e)=> {e.stopPropagation();approveAndSelectPreview(preview.previewId);}}
disabled={isApproving}
className="w-full luxury-subtle-button px-3 py-2 text-[9px] disabled:opacity-50"
>
{isApproving ? 'Approving...' : 'Approve & Select'}
</button>
</div>
)}
</div>
);
})}
{filteredPreviews.length===0 && availablePreviews.length===0 && (
<p className="text-center text-muted text-sm py-8">No active previews are available. Create or restore a preview before preparing introductory outreach.</p>
)}
{filteredPreviews.length===0 && availablePreviews.length > 0 && searchText.trim() && (
<p className="text-center text-muted text-sm py-8">No previews match your search.</p>
)}
</div>
</div>
</div>
{/* RIGHT COLUMN */}
<div className="space-y-6">
{/* Draft Messages */}
<div className="space-y-4">
<div className="flex items-center justify-between">
<h3 className="text-sm font-bold uppercase tracking-widest text-gold">Draft Messages</h3>
<button
type="button"
onClick={()=> refreshDraft()}
disabled={sending}
className="luxury-subtle-button px-4 py-2 text-[10px] disabled:opacity-50 flex items-center gap-2"
>
<SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
Regenerate Draft
</button>
</div>
<div className="relative">
<input
type="text"
value={emailSubject}
onChange={(e)=> setEmailSubject(e.target.value)}
placeholder=" "
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
Email Subject
</label>
</div>
<div className="relative">
<textarea
value={emailMessage}
onChange={(e)=> setEmailMessage(e.target.value)}
placeholder=" "
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[180px]"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
Email Message
</label>
</div>
<div className="relative">
<textarea
value={smsMessage}
onChange={(e)=> setSmsMessage(e.target.value)}
placeholder=" "
className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[100px]"
/>
<label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium">
SMS Message
</label>
</div>
</div>
{/* Consent & Basis */}
<div className="space-y-4 pt-4 border-t border-gold/15">
<h3 className="text-sm font-bold uppercase tracking-widest text-gold">Sending Basis</h3>
<div className="flex items-start gap-3">
<input
type="checkbox"
id="consent-confirmed"
checked={consentConfirmed}
onChange={(e)=> {setConsentConfirmed(e.target.checked);if (error) {setError('');}}}
className="mt-1 w-4 h-4 accent-gold rounded border-gold/30"
/>
<label
htmlFor="consent-confirmed"
className="text-[10px] text-muted uppercase tracking-wider leading-relaxed cursor-pointer select-none"
>
I have confirmed the appropriate basis for sending this introductory email and any selected SMS message.
</label>
</div>
<div className="relative">
<label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted absolute -top-3 left-4 bg-ivory px-2">
Consent Method
</label>
<select
value={consentMethod}
onChange={(e)=> setConsentMethod(e.target.value)}
className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso outline-none transition focus:border-gold text-sm appearance-none"
>
<option value="Phone conversation">Phone conversation</option>
<option value="Recipient requested information">Recipient requested information</option>
<option value="Existing business relationship">Existing business relationship</option>
<option value="Documented inferred-consent basis">Documented inferred-consent basis</option>
<option value="Other documented basis">Other documented basis</option>
</select>
<SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
</div>
</div>
{/* Status Panel */}
{(stageMessages.length > 0 || error) && (
<div className="bg-espresso text-soft rounded-xl p-5 space-y-3 shadow-inner">
<p className="text-[9px] uppercase tracking-[0.2em] text-mutedGold mb-2 font-bold">Status Log</p>
{stageMessages.map((stage)=> (
<div key={stage.id} className="flex items-center gap-3 text-xs">
{stage.status==='success' && <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-green-400" />}
{stage.status==='error' && <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5 text-red-400" />}
{stage.status==='info' && <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />}
<span
className={
stage.status==='error'
? 'text-red-300'
: stage.status==='success'
? 'text-green-300'
: 'text-soft/80'
}
>
{stage.message}
</span>
</div>
))}
{error && (
<div className="flex items-center gap-3 text-xs text-red-400 pt-2 border-t border-white/10 mt-2">
<SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5" />
<span>{error}</span>
</div>
)}
</div>
)}
</div>
</div>
</div>
{/* Modal Footer */}
<div className="shrink-0 border-t border-gold/15 bg-ivory px-6 py-5 sm:px-8 flex flex-wrap gap-4 justify-end">
<button
onClick={onClose}
disabled={sending}
className="luxury-subtle-button px-6 py-3 text-xs disabled:opacity-50"
>
Cancel
</button>
<button
onClick={()=> sendOutreach({includeSms: false})}
disabled={sending}
className="luxury-subtle-button px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
>
<SafeIcon icon={FiMail} className="w-4 h-4" />
Send Email Only
</button>
<button
onClick={()=> sendOutreach({includeSms: true})}
disabled={sending}
className="luxury-gold-button px-8 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
>
<SafeIcon icon={FiSend} className="w-4 h-4" />
Send Email + SMS
</button>
</div>
</div>
</div>
);
};
export default IntroOutreachComposer;